import { Injectable } from '@angular/core';
import { concat, Observable, of, throwError, timer } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { RenderEtaReading } from './render-eta';
import { RenderUploadModel } from './render-upload.inspector';
import {
  RenderCheckoutSession,
  RenderEntitlement,
  RenderFlowStep,
  RenderJob,
  RenderNotImplementedError,
  RenderPackFiles,
  RenderPackSizes,
  RenderPreviewResult,
  RenderSides,
  RenderVideosResult,
  renderVideosStatus,
} from './render-flow.model';
import {
  renderAttachmentUrl,
  RENDER_HERO_SAMPLE,
  RENDER_PREVIEW_STAGES,
  RenderOrientation,
  RenderPackKey,
  renderPackEncode,
  renderSampleById,
  renderVideoFileName,
} from './render.data';

// A preview or a full render reports progress and ends with a result; failures are observable
// errors (an Error with a user-facing message).
export type RenderRunEvent<T> =
  | { type: 'progress'; progress: number; statusText: string; sides?: RenderSides; eta?: RenderEtaReading }
  | { type: 'done'; result: T };

// An upload's bytes on their way; `done` once the backend has the whole model, with the id the
// backend knows it by (none from the mock, where nothing is sent).
export type RenderUploadEvent =
  | { type: 'progress'; sentBytes: number; totalBytes: number }
  | { type: 'done'; backendId?: string };

// What the flow is talking to: the browser-only mock, the dev render simulator (real upload and
// Firestore, DEV buttons in the renderer's place), or the real backend.
export type RenderBackendKind = 'mock' | 'simulator' | 'live';

// What the backend keeps about a job for the user's other tabs and devices: that it is bought
// (the page says so until the payment webhook does), and the step the user last looked at (the
// "new" dot).
export interface RenderJobFacts {
  entitlement?: RenderEntitlement;
  seenStep?: RenderFlowStep;
}

// The I/O half of the render flow. RenderFlowService owns the state machine and talks to the
// backend only through this class, so the stub below can be swapped for the live one without
// touching the pages. RenderModule decides which one is provided.
export abstract class RenderBackend {
  abstract readonly kind: RenderBackendKind;
  // The user's files, before any render: the first stage of the processing card.
  abstract uploadModel(upload: RenderUploadModel): Observable<RenderUploadEvent>;
  // Preview render of a model. `upload` carries the dropped files for an upload job, null for a
  // sample. `resume`: the page was reloaded while it ran — follow the run already going on the
  // backend rather than ask for another.
  abstract runPreview(job: RenderJob, upload: RenderUploadModel | null, resume?: boolean): Observable<RenderRunEvent<RenderPreviewResult>>;
  abstract createCheckout(job: RenderJob): Promise<RenderCheckoutSession>;
  // Resolves once the payment is confirmed; rejects with 'declined' / 'pending' style errors.
  abstract confirmPayment(job: RenderJob, sessionId: string): Promise<RenderEntitlement>;
  abstract runFullRender(job: RenderJob, resume?: boolean): Observable<RenderRunEvent<RenderVideosResult>>;
  // The "Optimize for platforms" ZIP: the chosen cells of the table (render.data.ts presets).
  abstract getPack(job: RenderJob, keys: ReadonlyArray<RenderPackKey>): Promise<RenderPackFiles>;
  // The user cancelled the preview: the backend stops the render and drops the model.
  abstract cancel(job: RenderJob): Promise<void>;

  // The id the backend will know an upload by, decided before its first byte, so the model's page
  // opens while the upload runs (the next uploadModel of `upload` uses it). None from the mock,
  // where nothing is sent. `upload` is unused on purpose here.
  public uploadId(upload: RenderUploadModel): string | undefined {
    return undefined;
  }

  // Every job of the signed-in user as the backend has it, again on each change — or null when
  // the backend keeps no list (the mock: its jobs live in this tab only).
  public watchJobs(): Observable<RenderJob[]> | null {
    return null;
  }

  // Stores `facts` with the job on the backend; nothing to store without a backend list.
  // `job` and `facts` are unused on purpose here.
  public remember(job: RenderJob, facts: RenderJobFacts): Promise<void> {
    return Promise.resolve();
  }

  // The user's new name for the model (the downloads page's "File name"); nothing to store without
  // a backend list. `job` and `name` are unused on purpose here.
  public rename(job: RenderJob, name: string): Promise<void> {
    return Promise.resolve();
  }

  // A finished preview the user takes out of the library: nothing runs any more, so nothing to stop
  // — the model just goes, as a cancel drops it.
  public discard(job: RenderJob): Promise<void> {
    return this.cancel(job);
  }

  // Whether getPack hands the source videos, one per click, in the ZIP's place (the mock, and the
  // samples everywhere). `job` is unused on purpose here.
  public packStandIn(job: RenderJob): boolean {
    return true;
  }

  // What the videos weigh, where the backend can say; null leaves the table its estimates.
  // `job` is unused on purpose here.
  public packSizes(job: RenderJob): Promise<RenderPackSizes | null> {
    return Promise.resolve(null);
  }
}

// The preview compressed, not instant (owner, 2026-09-23): 12 s through RENDER_PREVIEW_STAGES,
// which start at these offsets; progress creeps within each stage instead of jumping.
const RAMP_TICK_MS = 300;
const PREVIEW_STAGE_MS = [0, 2000, 4500];
const PREVIEW_DONE_MS = 12000;
const PREVIEW_STEPS = ramp(
  RENDER_PREVIEW_STAGES.map((stage, index): [number, number, string] => [PREVIEW_STAGE_MS[index], stage.from, `${stage.label}…`]),
  PREVIEW_DONE_MS,
  97,
);

// The live lines (renderVideosStatus), the videos a beat apart as they are on the renderer: one
// rendering while the other sets up, one done while the other finishes.
const FULL_RENDER_STEPS: ReadonlyArray<[number, number, string]> = [
  [0, 3, renderVideosStatus('waiting', 'preparing')],
  [1200, 30, renderVideosStatus('rendering', 'preparing')],
  [4200, 85, renderVideosStatus('done', 'rendering')],
  [5400, 96, renderVideosStatus('done', 'finishing')],
];
const FULL_RENDER_DONE_MS = 6200;

// Simulated backend: timers and the bundled sample renders. Nothing leaves the browser — an
// upload is inspected locally and its preview is a sample's stills (marked as a stand-in).
@Injectable()
export class MockRenderBackend extends RenderBackend {
  public readonly kind: RenderBackendKind = 'mock';

  // Timed from the files' size; nothing is sent.
  public uploadModel(upload: RenderUploadModel): Observable<RenderUploadEvent> {
    return simulateUpload(upload.totalBytes);
  }

  // `upload` is unused on purpose: the files stay on the device in this build.
  public runPreview(job: RenderJob, upload: RenderUploadModel | null): Observable<RenderRunEvent<RenderPreviewResult>> {
    const sampleId = job.source.kind === 'sample' ? job.source.sampleId : RENDER_HERO_SAMPLE;
    const sample = renderSampleById(sampleId) || renderSampleById(RENDER_HERO_SAMPLE);

    return schedule(PREVIEW_STEPS, PREVIEW_DONE_MS, () => ({
      preview: sample.stills,
      // An upload never reaches a renderer here; a sample's stills stand in and say so.
      standInSampleId: job.source.kind === 'upload' ? sample.id : undefined,
    }));
  }

  public async createCheckout(job: RenderJob): Promise<RenderCheckoutSession> {
    await wait(350);
    return { sessionId: `mock_${job.modelId}_${Date.now().toString(36)}` };
  }

  // `job` and `sessionId` are unused on purpose: every simulated payment goes through.
  public async confirmPayment(job: RenderJob, sessionId: string): Promise<RenderEntitlement> {
    await wait(650);
    return 'full';
  }

  public runFullRender(job: RenderJob): Observable<RenderRunEvent<RenderVideosResult>> {
    const sampleId = job.standInSampleId || (job.source.kind === 'sample' ? job.source.sampleId : RENDER_HERO_SAMPLE);
    const sample = renderSampleById(sampleId) || renderSampleById(RENDER_HERO_SAMPLE);
    return schedule(FULL_RENDER_STEPS, FULL_RENDER_DONE_MS, () => ({ videos: sample.videos }));
  }

  // Nothing encodes platform versions here: the pack is the source video of every orientation the
  // choice touches, saved as separate files, one per click (a ZIP cannot be built in the browser:
  // public storage sends no CORS headers, and feed-dev's allow no credentials for its CDN cookie —
  // measured 2026-10-16).
  public async getPack(job: RenderJob, keys: ReadonlyArray<RenderPackKey>): Promise<RenderPackFiles> {
    await wait(200);
    if (!job.videos) {
      throw new Error('no videos');
    }
    const videos = job.videos;
    const orientations = (['landscape', 'portrait'] as RenderOrientation[]).filter((orientation) =>
      keys.some((key) => key.endsWith(`.${orientation}`) && renderPackEncode(key)),
    );
    return {
      files: orientations.map((orientation) => ({
        url: renderAttachmentUrl(videos[orientation], renderVideoFileName(job.name, orientation)),
        name: renderVideoFileName(job.name, orientation),
        label: `${orientation} video`,
      })),
      standIn: true,
    };
  }

  // `job` is unused on purpose: nothing runs anywhere but in the browser, and unsubscribing already
  // stopped its timers.
  public cancel(job: RenderJob): Promise<void> {
    return Promise.resolve();
  }
}

// Where the real thing plugs in. Every method throws until the backend exists; the pages show
// RenderNotImplementedError as an explanatory screen rather than a spinner that never ends.
//
//   uploadModel     → UploadFileService's uploadBytesResumable ('state_changed': bytesTransferred /
//                     totalBytes — the numbers the library's upload progress shows)
//   runPreview      → WebUploadService.uploadFilesWithPreProcessing (customMetadata.framesToRender
//                     for a one-frame render) + UploadFileService.uploadModels / Firestore
//                     /userdata/{uid}/models/{id} for progress and cardPosterWeb/cardPosterMobile
//   createCheckout  → purchases-back: a one-time $9 price + Checkout Session
//                     (today only the $65/year subscription exists — CardsService.createCheckOutSession)
//   confirmPayment  → purchases-back: session status / entitlement on the model
//   runFullRender   → backend-triggered on the payment webhook (open question), watched like the preview
//   getPack         → web-backend POST /render_download: the ZIP of the chosen cells, as
//                     FirestoreRenderBackend.getPack takes it on dev (sizes: /render_download_info).
//                     The only download there is: the page's video cards are to watch (designer,
//                     2026-09-25)
//   cancel          → CardsService's POST /stop_render {user_id, model_id}: the backend stops the
//                     pods and deletes the model's Firestore document
//
// FirestoreRenderBackend (render-firestore-backend.ts, dev only) already does the upload, the
// preview and the full render this way, with DEV buttons instead of the renderer.
@Injectable()
export class LiveRenderBackend extends RenderBackend {
  public readonly kind: RenderBackendKind = 'live';

  public uploadModel(): Observable<RenderUploadEvent> {
    return throwError(() => new RenderNotImplementedError('Upload'));
  }

  public runPreview(): Observable<RenderRunEvent<RenderPreviewResult>> {
    return throwError(() => new RenderNotImplementedError('Preview render'));
  }

  public createCheckout(): Promise<RenderCheckoutSession> {
    return Promise.reject(new RenderNotImplementedError('Checkout'));
  }

  public confirmPayment(): Promise<RenderEntitlement> {
    return Promise.reject(new RenderNotImplementedError('Payment confirmation'));
  }

  public runFullRender(): Observable<RenderRunEvent<RenderVideosResult>> {
    return throwError(() => new RenderNotImplementedError('Full render'));
  }

  public getPack(): Promise<RenderPackFiles> {
    return Promise.reject(new RenderNotImplementedError('Platform pack'));
  }

  public cancel(): Promise<void> {
    return Promise.reject(new RenderNotImplementedError('Cancel'));
  }
}

// Stage points → a step every RAMP_TICK_MS, progress rising evenly from each stage's start to the
// next one's (the last stage up to `lastProgress` by `doneMs`).
function ramp(stages: ReadonlyArray<[number, number, string]>, doneMs: number, lastProgress: number): Array<[number, number, string]> {
  const steps: Array<[number, number, string]> = [];
  for (let at = 0; at < doneMs; at += RAMP_TICK_MS) {
    const index = stages.reduce((current, stage, i) => (stage[0] <= at ? i : current), 0);
    const [start, from, statusText] = stages[index];
    const next = stages[index + 1];
    const end = next ? next[0] : doneMs;
    const to = next ? next[1] : lastProgress;
    steps.push([at, Math.round(from + ((to - from) * (at - start)) / (end - start)), statusText]);
  }
  return steps;
}

// Emits the progress steps at their offsets, then the result (or the thrown error) at `doneMs`.
// Unsubscribing cancels everything, so a cancelled job leaves no timers behind.
function schedule<T>(steps: ReadonlyArray<[number, number, string]>, doneMs: number, finish: () => T): Observable<RenderRunEvent<T>> {
  const events: Array<[number, () => RenderRunEvent<T>]> = steps.map(([at, progress, statusText]) => [
    at,
    () => ({ type: 'progress' as const, progress, statusText }),
  ]);
  events.push([doneMs, () => ({ type: 'done' as const, result: finish() })]);

  return of(...events).pipe(
    mergeMap(([at, make]) => timer(at).pipe(take(1), map(() => make()))),
  );
}

const UPLOAD_TICK_MS = 80;

// A believable upload, quicker than a real one (owner, 2026-09-23): 2.6–5.5 s by size, an uneven
// speed that never runs backwards, the last tick exactly at the total. Also plays a sample's
// upload (RenderFlowService.beginUpload).
export function simulateUpload(totalBytes: number): Observable<RenderUploadEvent> {
  const durationMs = Math.min(5500, Math.max(2600, 2200 + (totalBytes / 1024 / 1024) * 25));
  const ticks = Math.max(1, Math.round(durationMs / UPLOAD_TICK_MS));
  const speeds = Array.from({ length: ticks }, (_, tick) => 0.55 + 0.45 * Math.abs(Math.sin(tick * 1.7 + 0.4)));
  const sum = speeds.reduce((total, speed) => total + speed, 0);
  let covered = 0;
  const sent = speeds.map((speed, tick) => (tick === ticks - 1 ? totalBytes : Math.round(totalBytes * ((covered += speed) / sum))));

  return concat(
    timer(UPLOAD_TICK_MS, UPLOAD_TICK_MS).pipe(
      take(ticks),
      map((tick): RenderUploadEvent => ({ type: 'progress', sentBytes: sent[tick], totalBytes })),
    ),
    of<RenderUploadEvent>({ type: 'done' }),
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
